import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

const GROUPS = [
    { id: 'all', name: 'כל קבוצות הפרזול', keywords: [] },
    { id: 'bauhaus', name: 'באוהאוס (Bauhaus)', keywords: ['באוהאוס'] },
    { id: 'basic', name: 'בייסיק / Basic', keywords: ['BASIC'] },
    { id: 'belgi', name: 'בלגי', keywords: ['בלגי'] },
    { id: 'zeroline', name: 'ZeroLine', keywords: ['ZEROLINE'] },
    { id: 'sky', name: 'SKY', keywords: ['SKY'] },
    { id: 'shutters', name: 'תריסים ומנועים', keywords: ['תריס', 'מנוע'] },
    { id: 'railings', name: 'מעקות זכוכית', keywords: ['מעקה'] },
    { id: 'misc', name: 'שונות / כללי', keywords: ['כללי', 'מבלט', 'ארוסול', 'SMART', 'מיגון', 'מנהטן', 'REYANERS', 'גזית', 'קליל דור'] }
];

export default function PerzolCatalog() {
    const { currentUser } = useAuth();
    const isAdmin = currentUser?.role === 'admin';

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);

    const [selectedGroup, setSelectedGroup] = useState('all');
    const [selectedSubCat, setSelectedSubCat] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);

    const [selectedImage, setSelectedImage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ pnum: '', description: '', unit: 'יחידה', unitPrice: '' });
    const [saving, setSaving] = useState(false);

    // Bulk markup state
    const [showMarkup, setShowMarkup] = useState(false);
    const [markupPercent, setMarkupPercent] = useState('');
    const [markupLoading, setMarkupLoading] = useState(false);

    // Inline price editing
    const [editingPrice, setEditingPrice] = useState(null);
    const [priceValue, setPriceValue] = useState('');

    const token = localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    }), [token]);

    // Fetch categories
    useEffect(() => {
        fetch(`${API_BASE}/api/perzol-catalog/categories`, { headers })
            .then(r => r.json())
            .then(data => setCategories(data))
            .catch(console.error);
    }, []); // eslint-disable-line

    // Group sub-categories from categories list
    const availableSubCategories = useMemo(() => {
        if (selectedGroup === 'all') return [{ id: 'all', name: 'כל המודלים' }];
        const currentGroup = GROUPS.find(g => g.id === selectedGroup);
        const matchedCats = categories.filter(c => {
            const upper = c.category.toUpperCase();
            if (currentGroup.id === 'misc') {
                return !GROUPS.some(g => g.id !== 'all' && g.id !== 'misc' && g.keywords.some(kw => upper.includes(kw.toUpperCase())));
            }
            return currentGroup.keywords.some(kw => upper.includes(kw.toUpperCase()));
        });
        return [{ id: 'all', name: 'הכל בקבוצה' }, ...matchedCats.map(c => ({ id: c.category, name: c.category }))];
    }, [selectedGroup, categories]);

    // Fetch items
    const fetchItems = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedSubCat !== 'all') params.set('category', selectedSubCat);
        if (searchQuery) params.set('search', searchQuery);
        params.set('page', page);
        params.set('limit', 200);

        fetch(`${API_BASE}/api/perzol-catalog?${params}`, { headers })
            .then(r => r.json())
            .then(data => {
                setItems(data.items || []);
                setTotal(data.total || 0);
                setLoading(false);
            })
            .catch(err => { console.error(err); setLoading(false); });
    }, [selectedSubCat, searchQuery, page, headers]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    // Client-side group filter when viewing all
    const filteredItems = useMemo(() => {
        if (selectedGroup === 'all' || selectedSubCat !== 'all') return items;
        const currentGroup = GROUPS.find(g => g.id === selectedGroup);
        return items.filter(item =>
            item.categories?.some(cat => {
                const upper = cat.toUpperCase();
                if (currentGroup.id === 'misc') {
                    return !GROUPS.some(g => g.id !== 'all' && g.id !== 'misc' && g.keywords.some(kw => upper.includes(kw.toUpperCase())));
                }
                return currentGroup.keywords.some(kw => upper.includes(kw.toUpperCase()));
            })
        );
    }, [items, selectedGroup, selectedSubCat]);

    const handleGroupChange = (gid) => { setSelectedGroup(gid); setSelectedSubCat('all'); setPage(1); };

    const handleImageError = (e) => {
        e.target.src = '/assets/logo-circle.png';
        e.target.className = 'w-1/2 h-1/2 object-contain opacity-20 grayscale mx-auto mt-10';
    };

    const getEffectivePrice = (item) => {
        if (item.userPrice !== null && item.userPrice !== undefined) return item.userPrice;
        return item.unitPrice || 0;
    };

    // Inline price save
    const savePrice = async (item) => {
        try {
            const res = await fetch(`${API_BASE}/api/perzol-catalog/${item._id}/price`, {
                method: 'PUT', headers,
                body: JSON.stringify({ userPrice: priceValue === '' ? null : parseFloat(priceValue) })
            });
            if (!res.ok) throw new Error('שגיאה בעדכון');
            setEditingPrice(null);
            fetchItems();
        } catch (err) { alert(err.message); }
    };

    // Bulk markup
    const applyBulkMarkup = async () => {
        if (!markupPercent) return;
        setMarkupLoading(true);
        try {
            const body = { markup: parseFloat(markupPercent) };
            const res = await fetch(`${API_BASE}/api/perzol-catalog/bulk-markup`, { method: 'POST', headers, body: JSON.stringify(body) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            alert(data.message);
            setShowMarkup(false);
            fetchItems();
        } catch (err) { alert(err.message); }
        setMarkupLoading(false);
    };

    // CRUD
    const openAddModal = () => {
        setEditingItem(null);
        setFormData({ pnum: '', description: '', unit: 'יחידה', unitPrice: '' });
        setShowModal(true);
    };
    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({ pnum: item.pnum, description: item.description, unit: item.unit, unitPrice: item.unitPrice || '' });
        setShowModal(true);
    };
    const handleSave = async () => {
        setSaving(true);
        try {
            const url = editingItem ? `${API_BASE}/api/perzol-catalog/${editingItem._id}` : `${API_BASE}/api/perzol-catalog`;
            const method = editingItem ? 'PUT' : 'POST';
            const body = { ...formData, unitPrice: parseFloat(formData.unitPrice) || 0 };
            const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'שגיאה');
            setShowModal(false);
            fetchItems();
        } catch (err) { alert(err.message); }
        setSaving(false);
    };
    const handleDelete = async (item) => {
        if (!window.confirm(`למחוק את ${item.pnum} - ${item.description}?`)) return;
        try {
            const res = await fetch(`${API_BASE}/api/perzol-catalog/${item._id}`, { method: 'DELETE', headers });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
            fetchItems();
        } catch (err) { alert(err.message); }
    };
    const canEdit = (item) => isAdmin || (item.source === 'user' && item.createdBy?._id === currentUser?.id);
    const canDelete = () => isAdmin;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" dir="rtl">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 sticky top-[72px] lg:top-[88px] z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <Link to="/professionals" className="inline-flex items-center gap-2 text-slate-500 hover:text-amber-600 mb-2 transition-colors font-bold text-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                חזרה לכלים
                            </Link>
                            <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">קטלוג פרזול ואביזרים</h1>
                            <p className="text-slate-500 mt-1">{total} פריטים • מחירים, נוסחאות כמות, ותמונות</p>
                        </div>
                        <div className="flex gap-3 items-center flex-wrap">
                            <div className="relative">
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                </span>
                                <input type="text" placeholder="חיפוש מק''ט או שם..." value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                                    className="w-full md:w-72 pl-4 pr-10 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition-all shadow-sm font-medium" />
                            </div>
                            {isAdmin && (
                                <button onClick={() => setShowMarkup(true)} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md whitespace-nowrap">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    עדכון מחירים %
                                </button>
                            )}
                            <button onClick={openAddModal} className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md whitespace-nowrap">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                הוסף פריט
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main */}
            <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col lg:flex-row gap-8 items-start">
                {/* Sidebar */}
                <div className="w-full lg:w-64 flex-shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 lg:sticky lg:top-[200px]">
                    <h2 className="font-black text-slate-800 text-lg mb-4 px-2 border-b border-slate-100 pb-3 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                        קבוצות מוצר
                    </h2>
                    <div className="flex flex-col gap-1 max-h-[60vh] overflow-y-auto pl-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full pr-1">
                        {GROUPS.map(group => (
                            <button key={group.id} onClick={() => handleGroupChange(group.id)}
                                className={`text-right w-full px-4 py-3 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-between ${selectedGroup === group.id ? 'bg-amber-50 text-amber-700 border border-amber-200 shadow-sm' : 'bg-transparent text-slate-600 border border-transparent hover:bg-slate-50 hover:text-amber-600'}`}>
                                <span className="truncate max-w-[85%]">{group.name}</span>
                                {selectedGroup === group.id && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Grid */}
                <div className="flex-1 w-full min-w-0">
                    {/* Sub-Category Chips */}
                    <div className="sticky top-[204px] z-30 bg-slate-50/95 backdrop-blur-md pb-4 pt-2 -mx-2 px-2 border-b border-slate-200 mb-6 flex flex-wrap gap-2 shadow-sm rounded-t-2xl lg:shadow-none">
                        {availableSubCategories.map(cat => (
                            <button key={cat.id} onClick={() => { setSelectedSubCat(cat.id); setPage(1); }}
                                className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all duration-300 border ${selectedSubCat === cat.id ? 'bg-amber-500 border-amber-500 text-white shadow-md' : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700'}`}>
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    <div className="mb-4 flex items-center justify-between text-slate-500 text-sm font-medium px-1">
                        <span>מוצגות {filteredItems.length} תוצאות</span>
                    </div>

                    {loading ? (
                        <div className="text-center py-20">
                            <div className="inline-block w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                            <p className="text-slate-500 mt-4 font-bold">טוען פרזול...</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-slate-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <h3 className="text-xl font-bold text-slate-700">לא נמצאו פריטי פרזול</h3>
                            <p className="text-slate-500 mt-2">הזן מק''ט אחר או שייך לקטגוריה רחבה יותר.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredItems.map((p) => {
                                const imagePath = p.image ? `/perzol_images/${p.image}` : null;
                                const effectivePrice = getEffectivePrice(p);
                                const hasCustomPrice = p.userPrice !== null && p.userPrice !== undefined;
                                const isEditingThisPrice = editingPrice === p._id;

                                return (
                                    <div key={p._id} className="group flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-amber-300 transition-all duration-500 overflow-hidden">
                                        {/* Image */}
                                        <div className={`relative h-56 p-4 border-b border-slate-100 flex items-center justify-center overflow-hidden bg-white ${imagePath ? 'cursor-pointer' : ''}`}
                                            onClick={() => imagePath && setSelectedImage(imagePath)}>
                                            {imagePath && (
                                                <div className="absolute inset-0 bg-amber-900/0 group-hover:bg-amber-900/5 transition-colors duration-300 z-10 flex items-center justify-center">
                                                    <div className="opacity-0 group-hover:opacity-100 bg-white text-amber-600 font-bold px-4 py-2 rounded-full shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 text-xs flex items-center gap-2">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>
                                                        הגדל תמונה
                                                    </div>
                                                </div>
                                            )}
                                            {imagePath ? (
                                                <img src={imagePath} alt={p.description} onError={handleImageError} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 mix-blend-multiply" />
                                            ) : (
                                                <div className="w-1/2 h-1/2 bg-slate-100 rounded-full flex items-center justify-center opacity-50">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="p-5 flex-1 flex flex-col bg-slate-50/50">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[11px] font-black uppercase tracking-widest text-amber-500 mb-1 flex items-center gap-1.5">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                                        מק"ט {p.pnum}
                                                    </div>
                                                    <h3 className="font-bold text-slate-900 text-lg leading-tight line-clamp-2">{p.description}</h3>
                                                </div>
                                                {canEdit(p) && (
                                                    <div className="flex gap-1 mr-2 flex-shrink-0">
                                                        <button onClick={() => openEditModal(p)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="עריכה">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                        </button>
                                                        {canDelete(p) && (
                                                            <button onClick={() => handleDelete(p)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="מחיקה">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Price Section */}
                                            <div className="mt-2 pt-2 border-t border-slate-200/60">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-slate-500 font-medium">מחיר ליחידה ({p.unit})</span>
                                                    {p.supplier && <span className="text-[9px] text-slate-400">{p.supplier}</span>}
                                                </div>
                                                {isEditingThisPrice ? (
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <input type="number" step="0.01" value={priceValue} onChange={e => setPriceValue(e.target.value)}
                                                            className="w-24 px-2 py-1 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500/20 outline-none font-mono" autoFocus
                                                            onKeyDown={e => { if (e.key === 'Enter') savePrice(p); if (e.key === 'Escape') setEditingPrice(null); }} />
                                                        <span className="text-xs text-slate-500">₪</span>
                                                        <button onClick={() => savePrice(p)} className="text-xs bg-amber-500 text-white px-2 py-1 rounded font-bold hover:bg-amber-600">שמור</button>
                                                        <button onClick={() => setEditingPrice(null)} className="text-xs text-slate-400 hover:text-slate-600">ביטול</button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 mt-1 cursor-pointer group/price" onClick={() => { setEditingPrice(p._id); setPriceValue(effectivePrice || ''); }}>
                                                        {effectivePrice > 0 ? (
                                                            <span className={`font-black text-xl tracking-tight ${hasCustomPrice ? 'text-emerald-600' : 'text-slate-800'}`}>
                                                                {effectivePrice.toFixed(2)} <span className="text-sm font-medium">₪</span>
                                                            </span>
                                                        ) : (
                                                            <span className="text-red-500 text-xs font-bold">חסר מחיר</span>
                                                        )}
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover/price:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                        {hasCustomPrice && p.unitPrice > 0 && (
                                                            <span className="text-[9px] text-slate-400 line-through">{p.unitPrice.toFixed(2)}₪</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Categories */}
                                            {p.categories && p.categories.length > 0 && (
                                                <div className="mt-auto pt-3 border-t border-slate-200/60">
                                                    <p className="text-[10px] text-slate-500 font-bold mb-1.5">דגמים נתמכים:</p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {p.categories.slice(0, 3).map((cat, i) => (
                                                            <span key={i} className="inline-flex px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[10px] text-slate-600 font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px]">{cat}</span>
                                                        ))}
                                                        {p.categories.length > 3 && (
                                                            <span className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-[10px] text-slate-500 font-medium">+{p.categories.length - 3} נוספים</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Image Modal */}
            {selectedImage && (
                <div className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10" onClick={() => setSelectedImage(null)}>
                    <button className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all duration-300" onClick={() => setSelectedImage(null)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    <div className="bg-white rounded-3xl overflow-hidden max-w-4xl max-h-full flex items-center justify-center p-8 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <img src={selectedImage} alt="Hardware Full View" className="max-w-full max-h-[80vh] object-contain" onError={(e) => { e.target.src = '/assets/logo-full.png'; }} />
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex justify-between items-center">
                            <h3 className="font-black text-slate-800 text-lg">{editingItem ? 'עריכת פריט' : 'הוספת פריט חדש'}</h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">מק"ט *</label>
                                    <input type="text" value={formData.pnum} onChange={e => setFormData(f => ({ ...f, pnum: e.target.value }))} disabled={!!editingItem}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none disabled:bg-slate-100 font-mono" placeholder="1084" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">מחיר ₪</label>
                                    <input type="number" step="0.01" value={formData.unitPrice} onChange={e => setFormData(f => ({ ...f, unitPrice: e.target.value }))}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none" placeholder="23.67" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">תיאור *</label>
                                <input type="text" value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none" placeholder="ידית דמה שקועה" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">יחידת מידה</label>
                                <select value={formData.unit} onChange={e => setFormData(f => ({ ...f, unit: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none bg-white">
                                    <option value="יחידה">יחידה</option>
                                    <option value="סט">סט</option>
                                    <option value="מטר">מטר</option>
                                    <option value="זוג">זוג</option>
                                </select>
                            </div>
                        </div>
                        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-bold text-sm">ביטול</button>
                            <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold text-sm shadow-md disabled:opacity-50 transition-all">
                                {saving ? 'שומר...' : (editingItem ? 'שמור שינויים' : 'הוסף פריט')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Markup Modal */}
            {showMarkup && (
                <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowMarkup(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-4">
                            <h3 className="font-black text-slate-800 text-lg">עדכון מחירים גלובלי</h3>
                            <p className="text-sm text-slate-500 mt-1">הוסף אחוז תוספת לכל המחירים</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">אחוז תוספת (%)</label>
                                <div className="flex items-center gap-3">
                                    <input type="number" step="0.1" value={markupPercent} onChange={e => setMarkupPercent(e.target.value)}
                                        className="w-32 px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-lg font-mono font-bold text-center" placeholder="10" />
                                    <span className="text-2xl font-bold text-slate-400">%</span>
                                </div>
                                {markupPercent && (
                                    <p className="text-sm text-slate-500 mt-3">
                                        דוגמה: פריט ב-100₪ → <span className="font-bold text-emerald-600">{(100 * (1 + parseFloat(markupPercent || 0) / 100)).toFixed(2)}₪</span>
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setShowMarkup(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-bold text-sm">ביטול</button>
                            <button onClick={applyBulkMarkup} disabled={markupLoading || !markupPercent}
                                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-sm shadow-md disabled:opacity-50 transition-all">
                                {markupLoading ? 'מעדכן...' : 'עדכן כל המחירים'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
